import React, { useEffect, useState } from 'react';
import api from '../api';
import './PageTable.css';

function Artists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [focusY, setFocusY] = useState(20);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin-artists');
      setArtists(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await api.patch(`/admin-artists/${id}/status`, { status });
      await fetchArtists();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const setFeaturedOrder = async (id, order) => {
    try {
      await api.patch(`/admin-artists/${id}/featured`, { featured_order: order === '' ? null : parseInt(order) });
      await fetchArtists();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal update featured');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setFocusY(20);
  };

  const cancelSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFocusY(20);
  };

  const cropImageToBlob = (file, targetAspect, focusYPercent) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let sx, sy, sw, sh;
        const imgAspect = img.width / img.height;
        if (imgAspect > targetAspect) {
          sh = img.height;
          sw = sh * targetAspect;
          sx = (img.width - sw) / 2;
          sy = 0;
        } else {
          sw = img.width;
          sh = sw / targetAspect;
          sx = 0;
          const maxSy = img.height - sh;
          sy = Math.max(0, maxSy) * (focusYPercent / 100);
        }
        const canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = Math.round(640 / targetAspect);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const res = await api.post('/admin-upload/artist-video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditForm((f) => ({ ...f, profile_video_url: res.data.url }));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal upload video');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const doUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const croppedBlob = await cropImageToBlob(selectedFile, 4 / 5, focusY);
      const formData = new FormData();
      formData.append('photo', croppedBlob, 'artist-photo.jpg');
      const res = await api.post('/admin-upload/artist-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditForm({ ...editForm, photo_url: res.data.url });
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal upload foto');
    } finally {
      setUploading(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/admin-artists/${id}`);
      setDetailModal(res.data);
      setSelectedFile(null);
      setPreviewUrl(null);
      setEditForm({
        name: res.data.name || '',
        bio: res.data.bio || '',
        genre: res.data.genre || '',
        photo_url: res.data.photo_url || '',
        instagram_url: res.data.instagram_url || '',
        tiktok_url: res.data.tiktok_url || '',
        spotify_url: res.data.spotify_url || '',
        youtube_channel_id: res.data.youtube_channel_id || '',
        contact_phone: res.data.contact_phone || '',
        contact_email: res.data.contact_email || '',
        profile_video_url: res.data.profile_video_url || '',
      });
    } catch (err) {
      alert('Gagal memuat detail artis');
    }
  };

  const closeDetail = () => {
    setDetailModal(null);
    setEditForm(null);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin-artists/${detailModal.id}`, editForm);
      await fetchArtists();
      closeDetail();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menyimpan perubahan');
    } finally {
      setSaving(false);
    }
  };

  const deleteArtist = async () => {
    if (!window.confirm(`Hapus permanen artis "${detailModal.name}"? Semua lagu, produk, dan data terkait ikut terhapus. Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/admin-artists/${detailModal.id}`);
      await fetchArtists();
      closeDetail();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus artis');
    }
  };

  return (
    <div>
      <h1 className="page-title">Artists</h1>
      <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
        Isi kolom "Urutan Tampil" dengan angka (1 = paling depan di homepage). Kosongkan untuk tidak menampilkan artis itu di homepage.
      </p>
      {loading ? (
        <p>Memuat...</p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Urutan Tampil</th>
                <th>Nama</th>
                <th>Email</th>
                <th>Genre</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {artists.map((a) => (
                <tr key={a.id}>
                  <td>
                    <input
                      type="number"
                      min="1"
                      defaultValue={a.featured_order || ''}
                      placeholder="-"
                      style={{ width: 60, padding: '4px 8px', fontSize: 13 }}
                      onBlur={(e) => setFeaturedOrder(a.id, e.target.value)}
                      title="Isi angka untuk urutan tampil di homepage (1 = paling depan). Kosongkan untuk tidak ditampilkan."
                    />
                  </td>
                  <td>
                    <button className="link-btn" onClick={() => openDetail(a.id)} style={{ background: 'none', border: 'none', color: '#C9A66B', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>
                      {a.name}
                    </button>
                  </td>
                  <td>{a.email || '-'}</td>
                  <td>{a.genre || '-'}</td>
                  <td>
                    <span className={`status-badge status-${a.status}`}>{a.status}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-approve"
                        disabled={a.status === 'approved' || updatingId === a.id}
                        onClick={() => updateStatus(a.id, 'approved')}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-reject"
                        disabled={a.status === 'rejected' || updatingId === a.id}
                        onClick={() => updateStatus(a.id, 'rejected')}
                      >
                        Reject
                      </button>
                      <button className="btn-edit" onClick={() => openDetail(a.id)}>Detail</button>
                    </div>
                  </td>
                </tr>
              ))}
              {artists.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: '#888' }}>
                    Belum ada artis terdaftar
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {detailModal && editForm && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>Detail Artis: {detailModal.name}</h2>

            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <div style={{ width: 120, height: 150, borderRadius: 8, overflow: 'hidden', background: '#1F1C46', flexShrink: 0 }}>
                {editForm.photo_url && (
                  <img src={editForm.photo_url} alt={detailModal.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                <div>Status akun: <b>{detailModal.account_status || '-'}</b></div>
                <div>Email: <b>{detailModal.email || '-'}</b></div>
                <div>Terdaftar: <b>{new Date(detailModal.created_at).toLocaleDateString('id-ID')}</b></div>
                <div style={{ marginTop: 8, color: '#E23FA0' }}>
                  Periksa foto profil di atas — jika tidak wajar/melanggar, hapus artis via tombol di bawah.
                </div>
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', marginBottom: 4 }}>Nama</label>
            <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>URL Foto Profil</label>
            <input type="url" value={editForm.photo_url} onChange={(e) => setEditForm({ ...editForm, photo_url: e.target.value })} />

            <div style={{ marginTop: 6 }}>
              <label
                style={{
                  display: 'inline-block',
                  fontSize: 13,
                  color: '#C9A66B',
                  border: '1px solid #C9A66B',
                  padding: '6px 14px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                📁 Pilih Foto dari Perangkat
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
              <span style={{ fontSize: 11, color: '#666', marginLeft: 8 }}>Maks 5MB, JPG/PNG/WEBP</span>
            </div>

            {previewUrl && (
              <div style={{ marginTop: 10, padding: 12, border: '1px dashed #C9A66B', borderRadius: 8 }}>
                <p style={{ fontSize: 12, color: '#C9A66B', marginBottom: 8 }}>Preview — atur posisi fokus, lalu upload:</p>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 96, height: 120, borderRadius: 6, overflow: 'hidden', background: '#1F1C46', flexShrink: 0 }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `center ${focusY}%` }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: '#888', marginBottom: 4 }}>
                      Posisi Fokus (atas ↔ bawah)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={focusY}
                      onChange={(e) => setFocusY(parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button type="button" className="btn-primary" onClick={doUpload} disabled={uploading}>
                        {uploading ? 'Mengupload...' : 'Gunakan Foto Ini'}
                      </button>
                      <button type="button" className="btn-secondary" onClick={cancelSelectedFile} disabled={uploading}>
                        Batal
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

<label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>Video Profil (opsional, maks 50MB, 1 video saja)</label>
            <input type="url" placeholder="URL video (atau upload di bawah)" value={editForm.profile_video_url || ''} onChange={(e) => setEditForm({ ...editForm, profile_video_url: e.target.value })} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 10 }}>
              <label style={{ display: 'inline-block', fontSize: 13, color: '#C9A66B', border: '1px solid #C9A66B', padding: '6px 14px', borderRadius: 6, cursor: 'pointer' }}>
                {uploadingVideo ? 'Mengupload...' : '📁 Upload Video dari Perangkat'}
                <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleVideoUpload} disabled={uploadingVideo} style={{ display: 'none' }} />
              </label>
              {editForm.profile_video_url && (
                <video src={editForm.profile_video_url} muted style={{ width: 60, height: 60, borderRadius: 6, objectFit: 'cover', background: '#000' }} />
              )}
            </div>
                        <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>Genre</label>
            <input type="text" value={editForm.genre} onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })} />

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>Bio</label>
            <textarea rows={8} style={{ resize: 'vertical' }} value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} />

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>Instagram URL</label>
            <input type="url" value={editForm.instagram_url} onChange={(e) => setEditForm({ ...editForm, instagram_url: e.target.value })} />

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>TikTok URL</label>
            <input type="url" value={editForm.tiktok_url} onChange={(e) => setEditForm({ ...editForm, tiktok_url: e.target.value })} />

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>Spotify URL</label>
            <input type="url" value={editForm.spotify_url} onChange={(e) => setEditForm({ ...editForm, spotify_url: e.target.value })} />

            <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>YouTube Channel ID</label>
            <input type="text" value={editForm.youtube_channel_id} onChange={(e) => setEditForm({ ...editForm, youtube_channel_id: e.target.value })} />

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #333' }}>
              <p style={{ fontSize: 11, color: '#C9A66B', marginBottom: 8 }}>
                🔒 Kontak Internal — khusus admin, TIDAK pernah tampil di halaman publik
              </p>
              <label style={{ display: 'block', fontSize: 13, color: '#aaa', marginBottom: 4 }}>Nomor HP/WhatsApp</label>
              <input type="tel" value={editForm.contact_phone} onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })} />

              <label style={{ display: 'block', fontSize: 13, color: '#aaa', margin: '10px 0 4px' }}>Email Kontak</label>
              <input type="email" value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} />
            </div>

            <div className="modal-actions" style={{ marginTop: 20, justifyContent: 'space-between', display: 'flex' }}>
              <button type="button" className="btn-delete" onClick={deleteArtist}>Hapus Permanen</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-secondary" onClick={closeDetail}>Batal</button>
                <button type="button" className="btn-primary" onClick={saveEdit} disabled={saving}>
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Artists;
